import React, {useCallback, useEffect, useState} from 'react';
import {MessageBar, MessageBarType} from '@fluentui/react';
import {IAdditionalData} from '../../models/Extension';
import {debounce} from "lodash";
import {
	useLazyGetExportExtensionOptionFriendlyNameQuery,
	useLazyGetExportExtensionOptionsQuery
} from "../../store/Api";
import AutocompleteSearchBox, {ISuggestionItem} from "../Common/AutocompleteSearchBox";
import {IconButton, Stack} from "@fluentui/react";

interface IExtensionFieldSearchProps {
	field: IAdditionalData;
	isDisabled?: boolean;
	extensionId: string;
	initialValue?: string;
	onChange: (value: string) => void;
	workspaceId?: string;
	// Value of this field's parentField (e.g. the selected company) used to scope the search — fetch contacts
	// under that company. Mirrors ExtensionDependentOption; without it a searchable dependent field ignores its
	// parent and the API falls back to the extension's default scope.
	parentFieldValue?: string;
}

const ExtensionFieldSearch: React.FC<IExtensionFieldSearchProps> = (props) => {
	const [searchText, setSearchText] = useState<string>();
	const [options, setOptions] = useState<{key: string, text: string, value: string}[]>([]);
	const [value, setValue] = useState<string | undefined>()
	const [valueLabel, setValueLabel] = useState<string | undefined>()
	const [defaultValueAssigned, setDefaultValueAssigned] = useState<boolean>();
	const [getExtensionOptions] = useLazyGetExportExtensionOptionsQuery();
	const [getOptionFriendlyName] = useLazyGetExportExtensionOptionFriendlyNameQuery();
	const [inProgress, setProgress] = React.useState(false);
	const [dynamicSuggestions, setDynamicSuggestions] = React.useState<ISuggestionItem[]>();
	// What an unfiltered lookup returns, fetched once when the field appears. Plenty of searchable
	// fields have few enough records to just show (a CRM's sales reps, an MSP's own companies), and
	// making somebody guess a search term to discover a list of thirty is the kind of dead end where
	// people give up and leave a required field blank. The chevron beside the box opens this list.
	const [baseSuggestions, setBaseSuggestions] = React.useState<ISuggestionItem[]>();
	const baseRequestedFor = React.useRef<string>();
	const [optionsError, setOptionsError] = React.useState<string | undefined>();

	const queryThreshold = 2;
	// Ceiling on what the unfiltered list may show. An extension that answers an empty search with
	// everything it has must not turn into an unusable dropdown; past this, typing is still the way
	// through, exactly as before.
	const baseSuggestionLimit = 100;
	// Scope lookups to the parent field's value (e.g. only contacts under the selected company) when this field
	// declares a parentField. When there is no parent (or no value yet) the search stays unscoped as before.
	const scopedAdditionalData = props.field.parentField && props.parentFieldValue
		? { [props.field.parentField]: props.parentFieldValue }
		: undefined;
	// One-shot: seed the selected value from the initial value (and fire onChange once).
	useEffect(() => {
		if(props.initialValue && !defaultValueAssigned){
			setValue(props.initialValue)
			setDefaultValueAssigned(true)
			if(!props.field.isFilterSearch) {
				props.onChange(props.initialValue)
			}
		}
	}, [props.initialValue])

	// Resolve the friendly-name label whenever the selected value or its parent scope changes. Kept separate from
	// the one-shot seeding above so the label is re-fetched with the correct scope if parentFieldValue arrives or
	// changes after the initial render (the lookup is scope-dependent via additionalData).
	useEffect(() => {
		if(!value) return
		if(props.field.field && props.extensionId) {
			getOptionFriendlyName({
				extensionId: props.extensionId,
				field: props.field.field,
				search: value,
				additionalData: scopedAdditionalData
			}).unwrap().then((result) => {if (result) setValueLabel(result)})
		}
		else {
			setValueLabel(value)
		}
	}, [value, props.parentFieldValue, props.field.parentField, props.extensionId])
	
	const load = async (value: string) => {
		if (value && value != '' && props.extensionId && props.extensionId != '' ) {
			getExtensionOptions({extensionId: props.extensionId, field: props.field.field, search: value, additionalData: scopedAdditionalData, workspaceId: props.workspaceId})
				.unwrap().then((result) => {
					// Extension failures come back as 200s with success=false — surface them instead of
					// silently showing no suggestions.
					setOptionsError(result?.success === false ? (result.error || 'The extension failed to search') : undefined)
					if (result?.success === false) setDynamicSuggestions(undefined)
					if (result?.options) {
						const options = result.options.map(
							(val: any) => new ExtensionFieldSearchOption(val.value, val.display, val.value)
						)
						setDynamicSuggestions(options)
					}
					setProgress(false)
				}
			).catch(() => {
				setOptionsError('The extension failed to search')
				setProgress(false)
			})
		}
	}

	/**
	 * The unfiltered list, fetched once per scope. Re-requested when the parent scope changes, since
	 * "every contact" means something different under a different company.
	 */
	const loadBase = async () => {
		if (!props.extensionId || !props.field.field) return;
		const scopeKey = `${props.extensionId}|${props.field.field}|${props.parentFieldValue ?? ''}`;
		if (baseRequestedFor.current === scopeKey) return;
		baseRequestedFor.current = scopeKey;

		setProgress(true);
		try {
			const result = await getExtensionOptions({
				extensionId: props.extensionId, field: props.field.field, search: '',
				additionalData: scopedAdditionalData, workspaceId: props.workspaceId,
			}).unwrap();
			const options = (result?.options ?? [])
				.slice(0, baseSuggestionLimit)
				.map((val: any) => new ExtensionFieldSearchOption(val.value, val.display, val.value));
			// Loaded, not shown. This runs as the field appears, so opening the list here would pop
			// callouts over a screen nobody has touched yet; the chevron is how it gets opened.
			setBaseSuggestions(options.length > 0 ? options : undefined);
		} catch {
			// An extension that will not answer an unfiltered lookup keeps the old behavior: type to search.
			setBaseSuggestions(undefined);
		}
		setProgress(false);
	};

	// Fetch the unfiltered list as the field appears, and again whenever its scope changes (a scope
	// change invalidates the list it was fetched for: "every contact" means something different under
	// a different company).
	//
	// A dependent field with no parent value yet is skipped: its list does not exist until the parent
	// is chosen, so asking for it costs a round trip to be told nothing, or an error.
	useEffect(() => {
		baseRequestedFor.current = undefined;
		setBaseSuggestions(undefined);
		if (props.field.parentField && !props.parentFieldValue) return;
		void loadBase();
	}, [props.extensionId, props.field.field, props.field.parentField, props.parentFieldValue]);

	const onChangeDynamic = async (newText?: string) => {
		if (!newText || newText.trim().length < queryThreshold) {
			// Fall back to the unfiltered list rather than showing nothing, so clearing the box returns
			// to the full set instead of a dead end.
			setDynamicSuggestions(baseSuggestions);
		} else {
			setProgress(true);
			await load(newText)
		}
	};

	const onSuggestionClicked = (suggestion: string | ISuggestionItem) => {
		if(typeof  suggestion === "string") {
			return;
		}
		setValue(suggestion.getKey());
		setValueLabel(suggestion.getSearchText());
		props.onChange(suggestion.getKey())
		setProgress(false)
	};

	const getPlaceholder = () => {
		if(value){
			// The friendly-name lookup returns '' when the extension declares no FriendlyNameUrl for the
			// field, so fall back to the raw value rather than showing "Selected Value: " with nothing.
			return `Selected Value: ${valueLabel || value}`
		}
		return `Search ${props.field.description}`
	}
	
	return (
		<React.Fragment>
			<Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
				<Stack.Item grow>
					<AutocompleteSearchBox
						placeholder={getPlaceholder()}
						onChange={async (_, newValue) => await onChangeDynamic(newValue)}
						onSuggestionClicked={onSuggestionClicked}
						value={searchText}
						suggestions={dynamicSuggestions}
						inProgress={inProgress}
						debounceTime={500}
						onClear={(ev) => {
							setValue(undefined)
							setValueLabel(undefined)
							setOptionsError(undefined)
							props.onChange('')
						}}
					/>
				</Stack.Item>
				<IconButton
					iconProps={{ iconName: dynamicSuggestions ? 'ChevronUp' : 'ChevronDown' }}
					title={baseSuggestions
						? (dynamicSuggestions ? 'Hide the list' : 'Show the available options')
						: 'No list is available for this field; type to search'}
					ariaLabel={dynamicSuggestions ? 'Hide the list' : 'Show the available options'}
					disabled={props.isDisabled || !baseSuggestions}
					onClick={() => setDynamicSuggestions(dynamicSuggestions ? undefined : baseSuggestions)}
				/>
			</Stack>
			{optionsError && <MessageBar messageBarType={MessageBarType.error}>{optionsError}</MessageBar>}
			{!props.field.optional && !value && <p className={'field-error-message'}>Field is required</p>}
		</React.Fragment>
	);
}

class ExtensionFieldSearchOption implements ISuggestionItem {
	constructor(
		private key: string,
		private text: string,
		private value: string,
	) {}
	getSearchText: () => string = () => {
		return this.text;
	};
	getSuggestionItem(query?: string) {
		return (
			<div key={this.key} className="suggestionItem">
				{this.text}
			</div>
		);
	}
	getKey: () => string = () => {
		return this.key;
	};
}

export default ExtensionFieldSearch;